/**
 * Client-side helpers for song media references (security finding S01).
 *
 * A song's media.src is one of:
 *  - a Firebase download URL with a token (public songs, and legacy songs not
 *    yet migrated) — the browser can play it directly;
 *  - a token-less `gs://bucket/path` URI (non-public songs after their tokens
 *    were revoked) — must be exchanged for a short-lived signed URL via the
 *    getMediaUrl Cloud Function;
 *  - a legacy external URL (old S3 bucket) — played directly.
 */

/** True when the stored reference can't be fed to <video src> as-is. */
export const needsAuthorizedUrl = (src: string | undefined | null): boolean => {
  if (!src) return false;
  return !/^https?:\/\//i.test(src.trim());
};

/** True for URLs that getMediaUrl can re-issue (Firebase / GCS hosted). */
export const isProjectStorageUrl = (src: string | undefined | null): boolean => {
  if (!src) return false;
  const s = src.trim();
  return (
    s.startsWith("gs://") ||
    /^https:\/\/firebasestorage\.googleapis\.com\//.test(s) ||
    /^https:\/\/([a-z0-9.-]+\.)?storage\.googleapis\.com\//.test(s) ||
    /^http:\/\/(localhost|127\.0\.0\.1):\d+\/v0\/b\//.test(s)
  );
};

/** Refresh a signed URL this long before it actually expires. */
export const SIGNED_URL_REFRESH_MARGIN_MS = 60 * 1000;

export const isStillValid = (expiresAt: number | null, now: number = Date.now()): boolean =>
  expiresAt === null || expiresAt - SIGNED_URL_REFRESH_MARGIN_MS > now;
